<template>
  <div>
    <LoadingSpinner class="page-loading" :isLoading="isLoading" />
  </div>

  <div class="container-fluid">
    <header class="main-header main-header-container">
      <h1 class="main-title">Noodmaterialen overzicht</h1>
      <div class="column-gap">
        <button @click="downloadAllMaterial()" type="button" class="btn btn-secondary">
          Download <font-awesome-icon :icon="['far', 'download']" />
        </button>
        <router-link to="/noodmaterialen/toevoegen">
          <button type="button" class="btn btn-secondary">
            Toevoegen <font-awesome-icon :icon="['far', 'plus']" />
          </button>
        </router-link>
      </div>
    </header>

    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col">
            <input type="text" v-model="searchTerm" class="form-control" name="search" @keyup="this.searchBarTyping()"
              placeholder="Zoeken op afmetingen of opmerking" />
          </div>
          <div class="col">
            <select v-model="isFilteredByLocation" class="form-control form-select" name="isFilteredByLocation"
              @change="searchSupplies()">
              <option :value="null" selected>{{ isFilteredByLocation === null ? 'Filteren op locatie' : 'Filter uitzetten' }}</option>
              <option v-for="location in locations" v-bind:key="location.id" :value="location.id">{{ location.name }}
              </option>
            </select>
          </div>
          <div class="col">
            <select v-model="isFilteredByGroup" class="form-control form-select" name="isFilteredByGroup"
              @change="searchSupplies()">
              <option :value="null" selected>{{ isFilteredByGroup === null ? 'Filteren op groep' : 'Filter uitzetten' }}
              </option>
              <option :value=0>Materiaal</option>
              <option :value=1>Materieel</option>
            </select>
          </div>
          <div class="col">
            <select v-model="isOrderedByStatus" class="form-control form-select" name="status" @change="searchSupplies()">
              <option :value="null" selected>{{ isOrderedByStatus === null ? 'Sorteren op status' : 'Sortering uitzetten' }}</option>
              <option :value=0>Beschikbaar</option>
              <option :value=2>Bezet</option>
              <option :value=3>Defect</option>
              <option :value=1>Onderweg</option>
            </select>
          </div>
        </div>

        <div class="overview-list">
          <table class="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Afvoer</th>
                <th>Verbruik</th>
                <th>Afmetingen</th>
                <th>Opmerkingen</th>
                <th>Huidige locatie</th>
                <th>Standplaats</th>
                <th>Op naam van</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="supply in supplies" v-bind:key="supply.id" class="overview-list-item"
                @click="$router.push(`/noodmaterialen/${supply.id}`)">
                <td class="overview-name">{{ supply.typeName }}</td>
                <td>{{ supply.disposal }}</td>
                <td>{{ supply.power }}</td>
                <td>{{ supply.dimensions }}</td>
                <td>{{ supply.comment }}</td>
                <td>{{ displayCorrectLocation(supply.currentLocation, supply.currentLocationString, supply.longitude,
                  supply.latitude) }}</td>
                <td>{{ supply.location }}</td>
                <td>{{ supply.underNameOf }}</td>
                <td>
                  <span :class="[
                    'status-circle',
                    supply.status === 0 ? 'status-available' :
                      supply.status === 2 ? 'status-used' :
                        supply.status === 3 ? 'status-faulty' : 'status-in-transit']">
                  </span>
                </td>

                <td class="overview-icon">
                  <font-awesome-icon :icon="['far', 'angle-right']" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { displayCorrectLocation, parseCoordinates } from '@/utils/coordinateFormatter';
import { saveAs } from "file-saver";
import { debounce } from 'vue-debounce'

export default {
  components: {
    LoadingSpinner,
  },
  name: "EmergencySuppliesOverview",
  data() {
    return {
      isLoading: false,
      locations: [],
      supplies: [],
      searchTerm: "",
      isOrderedByStatus: 0,
      isFilteredByGroup: null,
      isFilteredByLocation: null,
    };
  },
  mixins: [displayCorrectLocation],

  mounted() {
    this.getData();
    this.getLocationData();
  },

  created() {
    this.searchBarTyping = debounce(this.searchSupplies, '500ms')
  },

  methods: {
    displayCorrectLocation, 
    parseCoordinates, 
    async downloadAllMaterial() {
      const result = await this.$axiosInstance.get(
        `${process.env.VUE_APP_API}/EmergencyEquipment/exportAll`,
        { responseType: "blob" }
      );

      const now = new Date();
      const fileName = `EmergencyEquipmentExport-${now.toLocaleDateString()}.xlsx`;

      const blob = new Blob([result.data]);
      saveAs(blob, fileName);
    },
    async getData() {
      this.isLoading = true;

      this.$axiosInstance
        .get(`${process.env.VUE_APP_API}/EmergencyEquipment`)
        .then(async (response) => {
          this.supplies = response.data;
        })
        .catch(() => {
          this.$toast.open({
            message: "Noodmaterialen ophalen niet gelukt. Probeer het a.u.b. opnieuw.",
            type: "error",
            position: "top-right",
            duration: 5000,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getLocationData() {
      this.isLoading = true;

      this.$axiosInstance
        .get(`${process.env.VUE_APP_API}/EmergencyEquipmentLocation`)
        .then((response) => {
          if ((response.data !== "")) {
            this.locations = response.data;
          }
        })
        .catch(() => {
          this.$toast.open({
            message: "Locaties konden niet worden opgehaald.",
            type: "error",
            position: "top-right",
            duration: 5000,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async searchSupplies() {
      this.isLoading = true;

      this.$axiosInstance
        .get(`${process.env.VUE_APP_API}/EmergencyEquipment/search`, {
          params: {
            query: this.searchTerm,
            equipmentGroup: this.isFilteredByGroup,
            status: this.isOrderedByStatus,
            locationId: this.isFilteredByLocation,
          }
        })
        .then(async (response) => {
          this.supplies = response.data;
        })
        .catch(() => {
          this.$toast.open({
            message: "Geen resultaten gevonden. Probeer het a.u.b. opnieuw.",
            type: "error",
            position: "top-right",
            duration: 5000,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.column-gap{
    column-gap: 20px;
    display: flex;
}

.main-header-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.overview-list {
  .overview-list-item {
    margin-bottom: -$card-border-width;
    padding: $spacer;
    background-color: $white;
    color: $body-color;

    &:nth-child(even) {
      background-color: tint-color($secondary, 96%);
    }

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      cursor: pointer;
      color: $body-color;
      background-color: tint-color($primary, 90%);
    }
  }

  .status-circle {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: inline-block;
  }

  .status-available {
    background-color: green;
  }

  .status-faulty {
    background-color: red;
  }

  .status-used {
    background-color: orange;
  }

  .status-in-transit {
    background-color: blue;
  }
}
</style>